import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Eye, EyeOff, MailCheck } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/Button';
import { Input, Field } from '@/components/ui/Input';
import PhoneField, { isValidPhoneNumber } from '@/components/ui/PhoneField';
import { useAuth } from '@/lib/auth';
import { errorMessage } from '@/lib/utils';
import { useToast } from '@/components/ui/Toast';
import { AuthSplitLayout } from '@/pages/Login';
import GoogleAuthButton from '@/components/GoogleAuthButton';

export default function Register() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // NEW: email verification. Registration no longer logs the user in — it
  // creates the account and sends a verification email, so we show a
  // "check your email" confirmation screen instead of navigating away.
  const [submittedEmail, setSubmittedEmail] = useState(null);
  const { register, resendVerification } = useAuth();
  const { toast } = useToast();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    if (!form.phone || !isValidPhoneNumber(form.phone)) {
      setError('Please enter a valid phone number with the correct country code');
      setLoading(false);
      return;
    }
    try {
      const data = await register(form);
      setSubmittedEmail(data.email || form.email);
    } catch (err) {
      setError(errorMessage(err.response?.data?.detail, 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      await resendVerification(submittedEmail);
      toast({ title: 'Verification email sent', description: submittedEmail, variant: 'success' });
    } catch (err) {
      toast({ title: 'Could not resend right now', description: err.response?.data?.detail || 'Try again shortly', variant: 'error' });
    }
  };

  if (submittedEmail) {
    return (
      <AuthSplitLayout mode="customer">
        <div className="lg:hidden mb-10"><Logo /></div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-volt mb-3 flex items-center gap-2"><MailCheck size={12} /> Almost there</div>
        <h1 className="font-heading font-black text-4xl sm:text-5xl uppercase tracking-tight leading-none">
          Check your<br />inbox.
        </h1>
        <p className="mt-4 text-white/60 text-sm">
          We've sent a verification link to <span className="text-white font-medium">{submittedEmail}</span>.
          Click it to activate your account, then come back and sign in.
        </p>
        <div className="mt-8 space-y-3">
          <Button className="w-full" onClick={handleResend} data-testid="resend-verification-button">
            Resend verification email
          </Button>
          <Link to="/login" className="block text-center text-[10px] uppercase tracking-[0.25em] text-white/40 hover:text-volt pt-2" data-testid="goto-login-after-register">
            ← Back to sign in
          </Link>
        </div>
      </AuthSplitLayout>
    );
  }

  return (
    <AuthSplitLayout mode="customer">
      <div className="lg:hidden mb-10"><Logo /></div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-volt mb-3">Join the squad</div>
      <h1 className="font-heading font-black text-4xl sm:text-5xl uppercase tracking-tight leading-none">
        Create your<br />player account.
      </h1>
      <p className="mt-3 text-white/50 text-sm">Already a member? <Link to="/login" className="text-volt hover:underline" data-testid="goto-login">Sign in</Link>.</p>

      <form onSubmit={handleSubmit} className="mt-10 space-y-5" data-testid="register-form">
        <Field label="Full Name" htmlFor="name" error={error}>
          <Input id="name" data-testid="register-name" required value={form.name} onChange={set('name')} placeholder="Karthik R." />
        </Field>
        <Field label="Email" htmlFor="email">
          <Input id="email" type="email" data-testid="register-email" required value={form.email} onChange={set('email')} placeholder="you@example.com" />
        </Field>
        <Field label="Phone (pick country + number)" htmlFor="phone" hint="Used for WhatsApp booking confirmations">
          <PhoneField
            value={form.phone}
            onChange={(v) => setForm({ ...form, phone: v })}
            defaultCountry="IN"
            required
            placeholder="98765 43210"
            testId="register-phone"
          />
        </Field>
        <Field label="Password" htmlFor="password" hint="8+ characters, with an uppercase letter, lowercase letter, number, and special character.">
          <div className="relative">
            <Input
              id="password"
              type={show ? 'text' : 'password'}
              data-testid="register-password"
              required
              minLength={8}
              value={form.password}
              onChange={set('password')}
              placeholder="At least 8 characters"
              className="pr-12"
            />
            <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-volt">
              {show ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </Field>
        <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit-button">
          {loading ? 'Creating…' : <>Create Account <ArrowRight size={16} /></>}
        </Button>

        <div className="mt-6">
  <div className="relative mb-4">
    <div className="absolute inset-0 flex items-center">
      <div className="w-full border-t border-white/10"></div>
    </div>

    <div className="relative flex justify-center text-xs uppercase">
      <span className="bg-obsidian px-2 text-white/50">
        Or
      </span>
    </div>
  </div>

  <GoogleAuthButton />
</div>
        <div className="text-center pt-2">
          <Link to="/" className="text-[10px] uppercase tracking-[0.25em] text-white/40 hover:text-volt">← Back to home</Link>
        </div>
      </form>
    </AuthSplitLayout>
  );
}
